import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SopStrip } from "@/components/SopStrip";
import { useAuth } from "@/context/AuthContext";
import {
  CheckCircle2,
  AlertTriangle,
  Circle,
  Lock,
  Unlock,
  Loader2,
  ShieldCheck,
  GitCompareArrows,
} from "lucide-react";

const STATE_STYLE = {
  closed: "bg-emerald-400/20 border-emerald-400/50 text-emerald-200",
  attention: "bg-rose-400/20 border-rose-400/50 text-rose-200",
  open: "bg-amber-400/15 border-amber-400/40 text-amber-200",
  "no-activity": "bg-white/[0.03] border-white/10 text-muted-foreground",
};

export default function DayClose() {
  const { currency } = useCurrency();
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const canClose = ["super admin", "admin", "supervision"].includes(role);
  const canReopen = ["super admin", "admin"].includes(role);
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get(`/day-close/status?date=${date}&currency=${currency}`),
        api.get("/day-close/history?days=30"),
      ]);
      setStatus(s.data);
      setNotes(s.data.notes || "");
      setHistory(h.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load day-close status");
    } finally {
      setLoading(false);
    }
  }, [date, currency]);

  useEffect(() => {
    load();
  }, [load]);

  const closeDay = async (force = false) => {
    setBusy(true);
    try {
      await api.post("/day-close", { date, notes, force, currency });
      toast.success(`${date} closed and locked`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not close the day");
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await api.delete(`/day-close/${date}`);
      toast.success(`${date} reopened`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reopen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="day-close-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Day-End Control</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Definitive checkpoints that prove the day has been fully booked before it's locked.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/recon?status=all&from=${date}&to=${date}`)}
            className="gap-2 h-10"
            data-testid="dayclose-open-txns"
          >
            <GitCompareArrows className="h-4 w-4" />
            Day's bank lines
          </Button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="day-close-date"
            className="h-10 rounded-md bg-background border border-input px-2 text-xs"
          />
        </div>
      </div>

      <SopStrip date={date} currency={currency} nonce={status?.closed ? 1 : 0} />

      {loading || !status ? (
        <div className="py-24 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["Bank lines", status.stats.bank_lines, "dayclose-stat-lines"],
              ["Unmatched", status.stats.unmatched, "dayclose-stat-unmatched"],
              ["Expenses booked", status.stats.expenses, "dayclose-stat-expenses"],
              ["Cash in", formatMoney(status.stats.inflow, currency, { compact: true }), "dayclose-stat-in"],
              ["Cash out", formatMoney(status.stats.outflow, currency, { compact: true }), "dayclose-stat-out"],
            ].map(([label, value, tid]) => (
              <Card key={tid} className="p-4" data-testid={tid}>
                <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
                <p className="font-heading text-xl mt-1.5 font-mono">{value}</p>
              </Card>
            ))}
          </div>

          <Card className="p-5 space-y-3" data-testid="dayclose-checklist">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                Checkpoints · {date}
              </p>
              {status.closed ? (
                <span
                  className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border bg-emerald-400/10 border-emerald-400/40 text-emerald-300"
                  data-testid="dayclose-locked-badge"
                >
                  Closed by {status.closed_by}
                </span>
              ) : status.ready_to_close ? (
                <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border bg-brand/10 border-brand/40 text-brand">
                  Ready to close
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border bg-amber-400/10 border-amber-400/40 text-amber-300">
                  Open items
                </span>
              )}
            </div>
            <div className="divide-y divide-white/[0.04]">
              {status.checks.map((c) => (
                <div key={c.id} className="flex items-start gap-3 py-3" data-testid={`check-${c.id}`}>
                  {c.ok ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                  ) : c.blocking ? (
                    <AlertTriangle className="h-5 w-5 text-rose-400 shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-amber-400 shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm">
                      {c.label}
                      {c.blocking && !c.ok ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-rose-300">blocking</span>
                      ) : null}
                    </p>
                    <p className="text-xs text-muted-foreground">{c.detail}</p>
                  </div>
                </div>
              ))}
            </div>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Day-end notes (what was reviewed, exceptions, approvals)"
              className="min-h-[72px]"
              data-testid="dayclose-notes"
              disabled={status.closed}
            />
            <div className="flex items-center gap-2 flex-wrap">
              {status.closed ? (
                <Button
                  variant="outline"
                  onClick={reopen}
                  disabled={busy || !canReopen}
                  title={canReopen ? undefined : "Only a super admin can reopen a signed-off day"}
                  className="gap-2"
                  data-testid="dayclose-reopen-btn"
                >
                  <Unlock className="h-4 w-4" />
                  Reopen day
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => closeDay(false)}
                    title={canClose ? undefined : "Only Supervision or a super admin can sign off a day"}
                    disabled={busy || !status.ready_to_close || !canClose}
                    className="gap-2"
                    data-testid="dayclose-close-btn"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                    Close & lock day
                  </Button>
                  {!status.ready_to_close && (
                    <Button
                      variant="outline"
                      onClick={() => closeDay(true)}
                      disabled={busy || !canClose}
                      className="gap-2"
                      data-testid="dayclose-force-btn"
                    >
                      <ShieldCheck className="h-4 w-4" />
                      Close with exception note
                    </Button>
                  )}
                </>
              )}
            </div>
          </Card>

          {history && (
            <Card className="p-5 space-y-3" data-testid="dayclose-history">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
                  Last 30 days
                </p>
                <div className="flex gap-3 text-[11px] text-muted-foreground">
                  <span>{history.summary.closed} closed</span>
                  <span>{history.summary.attention} need attention</span>
                  <span>{history.summary.open} open</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {history.days.map((d) => (
                  <button
                    key={d.date}
                    onClick={() => setDate(d.date)}
                    title={`${d.date} · ${d.state} · ${d.lines} line(s), ${d.unmatched} unmatched`}
                    data-testid={`history-day-${d.date}`}
                    className={`h-10 w-[60px] rounded-md border text-[10px] font-mono transition-transform hover:scale-105 ${
                      STATE_STYLE[d.state]
                    } ${d.date === date ? "ring-2 ring-brand/60" : ""}`}
                  >
                    {d.date.slice(5)}
                  </button>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
